// src/pages/DashboardNavigation/components/NavigationDrawer.jsx

import React, { useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaChevronRight,
  FaSearch,
  FaTimes,
  FaUser,
  FaUserTie,
  FaShieldAlt,
} from "react-icons/fa";

import {
  normalizePath,
  normalizeText,
} from "../utils/dashboardNavigationUtils";

const SECTION_ICONS = {
  user: FaUser,
  enseignant: FaUserTie,
  admin: FaShieldAlt,
};

export default function NavigationDrawer({
  open,
  sections,
  activeSectionId,
  onChangeSection,
  currentPath,
  onGoTo,
  onClose,
}) {
  const [search, setSearch] = useState("");

  const currentNorm = normalizePath(currentPath);

  const activeSection = useMemo(() => {
    return (
      sections.find((section) => section.id === activeSectionId) ||
      sections[0] ||
      null
    );
  }, [activeSectionId, sections]);

  const query = normalizeText(search);

  const filteredItems = useMemo(() => {
    if (!activeSection) return [];

    if (!query) return activeSection.items;

    return activeSection.items.filter((item) => {
      const label = normalizeText(item.label);
      return label.includes(query);
    });
  }, [activeSection, query]);

  if (!open) return null;

  return (
    <div className="mt-2 overflow-hidden rounded-[1.35rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/70 dark:border-slate-800 dark:bg-slate-950 dark:shadow-black/20">
      <div className="border-b border-slate-200 bg-white/95 p-3 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">
              Navigation complète
            </p>

            <h3 className="mt-1 text-base font-bold text-slate-950 dark:text-white">
              Pages disponibles
            </h3>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-900 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
            aria-label="Fermer la navigation complète"
          >
            <FaTimes />
          </button>
        </div>

        {sections.length > 1 && (
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {sections.map((section) => {
              const active = section.id === activeSection?.id;
              const Icon = SECTION_ICONS[section.id] || FaChevronRight;

              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => {
                    onChangeSection(section.id);
                    setSearch("");
                  }}
                  className={[
                    "flex min-h-11 shrink-0 items-center gap-2 rounded-2xl px-3 text-sm font-bold ring-1 transition",
                    active
                      ? "bg-slate-950 text-white ring-slate-950 shadow-sm dark:bg-white dark:text-slate-950 dark:ring-white"
                      : "bg-slate-50 text-slate-600 ring-slate-200 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-800 dark:hover:bg-slate-800",
                  ].join(" ")}
                >
                  <Icon className="text-xs" />
                  <span>{section.label}</span>

                  <span
                    className={[
                      "rounded-full px-2 py-0.5 text-[11px]",
                      active
                        ? "bg-white/15 text-white dark:bg-slate-950/10 dark:text-slate-700"
                        : "bg-white text-slate-500 dark:bg-slate-950 dark:text-slate-400",
                    ].join(" ")}
                  >
                    {section.items.length}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {activeSection && (
          <div className="mt-3 rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-200 dark:bg-slate-900/70 dark:ring-slate-800">
            <p className="text-sm font-bold text-slate-950 dark:text-white">
              {activeSection.label}
            </p>

            <p className="mt-0.5 text-xs leading-5 text-slate-500 dark:text-slate-400">
              {activeSection.description}
            </p>
          </div>
        )}

        <div className="relative mt-3">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400" />

          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher par nom de page..."
            className="min-h-11 w-full rounded-2xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm font-semibold text-slate-900 outline-none transition focus:ring-2 focus:ring-sky-300 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
          />
        </div>
      </div>

      <div className="max-h-[48vh] overflow-y-auto p-3">
        {!activeSection ? (
          <div className="rounded-2xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
            Aucune section disponible.
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
            Aucune page trouvée dans l’onglet {activeSection.label}.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {filteredItems.map((item) => {
              const active = item.norm === currentNorm;

              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => onGoTo(item.path)}
                  aria-current={active ? "page" : undefined}
                  className={[
                    "flex min-h-12 w-full items-center justify-between gap-3 rounded-2xl px-3 py-2 text-left text-sm font-bold transition",
                    active
                      ? "bg-slate-950 text-white shadow-sm dark:bg-white dark:text-slate-950"
                      : "bg-slate-50 text-slate-700 ring-1 ring-slate-200/80 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-800 dark:hover:bg-slate-800",
                  ].join(" ")}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {item.label}
                  </span>

                  {active ? (
                    <FaCheckCircle className="shrink-0 text-emerald-400 dark:text-emerald-600" />
                  ) : (
                    <FaChevronRight className="shrink-0 text-xs opacity-50" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}